import { ChangeEvent, FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Category, SearchSuggestion } from '@/__generated_graphql__/graphql';
import { Filters } from '@/components/Filters/Filters';
import { Portal } from '@/components/Portal/Portal';
import { SearchInput } from '@/components/SearchInput/SearchInput';
import { Overlay } from '@/components/ui/Overlay/Overlay';
import { ParticipantEnum } from '@/constants/common';
import { useGetCategories } from '@/hooks/useGetCategories';
import { useGetSuggestions } from '@/hooks/useGetSuggestions';
import { useNavigate } from '@/hooks/useNavigate';
import { useSearchInputPosition } from '@/hooks/useSearchInputPosition';
import { useSearchRoute } from '@/hooks/useSearchRoute';
import { useParticipant } from '@/providers/ParticipantProvider';
import { FormValues, ResultPageInitialProps } from '@/types';
import { debounce } from '@/utils/helpers/debounce';

import * as S from './SearchBar.styles';
import { SearchBarDropdown } from './SearchBarDropdown';

interface SearchBarProps {
  isBig?: boolean;
}

const SEARCH_TERM = 'searchTerm';

export const SearchBar: FC<SearchBarProps> = ({ isBig = false }) => {
  const [query, setQuery] = useState<string | Category>('');
  const [isOpen, setIsOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { navigate } = useNavigate();

  const { participant } = useParticipant();
  const { t } = useTranslation();

  const SEARCH = useSearchRoute();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { searchInputRef, spaceBarRef, dropdownRef } = useSearchInputPosition({
    isOpen,
    isBig,
    isHeaderFixed: false
  });

  const { data: suggestionsData, fetchMore } = useGetSuggestions({
    query: ''
  });

  const { data: categories } = useGetCategories();

  const handleDebounceQuery = useCallback(debounce(fetchMore, 100), []);

  const handleChangeSuggestions = (searchTerm: string) => {
    if (searchTerm) return handleDebounceQuery(searchTerm);
    fetchMore(searchTerm);
    handleDebounceQuery.cancel();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement> | string) => {
    let value: string = '';
    if (typeof e === 'string') value = e;
    else value = e.target.value;
    setQuery(value);
    handleChangeSuggestions(value);
  };

  const handleClose = () => setIsOpen(false);

  const handleFocus = () => {
    setIsOpen(true);
    if (!isBig) inputRef.current?.blur();
  };

  const handleClear = () => {
    setQuery('');
    handleChangeSuggestions('');
  };

  const handleToggleFilters = () => {
    setIsFiltersOpen(prev => !prev);
    inputRef.current?.blur();
    setIsOpen(false);
  };

  const handleSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    navigate({
      route: SEARCH,
      params: { [SEARCH_TERM]: typeof query === 'object' ? query.name : query }
    });
  };

  const handleSubmitFilter = (data: FormValues) => {
    const transformedData: Partial<
      Record<keyof ResultPageInitialProps, string | string[]>
    > = {
      categoryIds: data.categories.map(item => item.id),
      regionIds: data.regions.map(item => item.id),
      educationIds: data.educations?.map(item => item.id),
      experienceIds: data.experiences?.map(item => item.id),
      salaryFrom: data.salaryFrom.toString(),
      salaryTo: data.salaryTo.toString()
    };

    navigate({ route: SEARCH, params: transformedData });
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.searchTerm);
    navigate({
      route: SEARCH,
      params: { [SEARCH_TERM]: suggestion.searchTerm }
    });
  };

  const handleCategoryClick = (category: Category) => {
    setQuery(category);
    navigate({
      route: SEARCH,
      params: {
        categoryIds: category.id
      }
    });
  };

  const handleKeyDown = (input: HTMLInputElement | null) => {
    if (input) {
      handleChange(input.value);
      navigate({
        route: SEARCH,
        params: { [SEARCH_TERM]: input.value }
      });
    }
  };

  const translationKey: string =
    participant === ParticipantEnum.RESUME ? 'resume_button' : 'vacancy_button';

  const baseInputProps = {
    readonly: !isBig,
    isBig,
    query: typeof query === 'object' ? query.name : query,
    isOpen,
    placeholder: t('SearchBar.placeholder'),
    submitLabel: t(`SearchBar.${translationKey}`),
    onSubmit: handleSearch,
    onFocus: handleFocus,
    onChange: handleChange,
    onReset: handleClear,
    onFilter: handleToggleFilters,
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleKeyDown(inputRef.current);
    }
  };

  const inputElement = (
    <SearchInput
      {...baseInputProps}
      searchInputRef={searchInputRef}
      inputRef={inputRef}
    />
  );

  return (
    <S.Wrapper>
      <Portal id="search-input-overlay">
        {isOpen && <Overlay onClick={() => setIsOpen(false)} zIndex={5} />}
      </Portal>

      <Portal id="search-input-filter">
        <Filters
          isOpen={isFiltersOpen}
          onClose={handleToggleFilters}
          onSubmit={handleSubmitFilter}
        />
      </Portal>

      <div
        data-testid="search-input-space"
        ref={spaceBarRef}
        style={{ opacity: '0' }}
      >
        <SearchInput {...baseInputProps} />
      </div>

      <Portal id="search-input">{inputElement}</Portal>
      <Portal id="search-input-dropdown">
        <SearchBarDropdown
          onCategory={handleCategoryClick}
          onSuggestion={handleSuggestionClick}
          onClear={handleClear}
          onClose={handleClose}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          searchSuggestion={suggestionsData}
          categories={categories}
          isOpen={isOpen}
          query={query}
          elRef={dropdownRef}
          isModal={!isBig}
        />
      </Portal>
    </S.Wrapper>
  );
};
